import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api';
import toastService from '../../services/toastService';
import {
  Calculator,
  AlertTriangle,
  CheckCircle,
  Edit3,
  Save,
  X,
  Brain,
  TrendingUp,
  Shield,
  Eye,
  Lock,
  Unlock
} from 'lucide-react';

interface Control {
  id: number;
  control_name: string;
  design_effectiveness: 'Effective' | 'Needs Improvement' | 'Ineffective';
  operating_effectiveness: 'Effective' | 'Needs Improvement' | 'Ineffective';
  design_effectiveness_score: number;
  operating_effectiveness_score: number;
  effectiveness_weight: number;
  control_type: string;
}

interface RiskData {
  id: number;
  risk_name: string;
  inherent_likelihood: number;
  inherent_impact: number;
  inherent_risk_score: number;
  inherent_risk_level: string;
  controls: Control[];
}

interface ResidualRiskResult {
  residual_likelihood: number;
  residual_impact: number;
  residual_risk_score: number;
  residual_risk_level: string;
  risk_reduction_percentage: number;
  control_effectiveness_summary: {
    total_controls: number;
    effective_controls: number;
    overall_effectiveness: number;
    weighted_effectiveness: number;
  };
  manual_override?: {
    enabled: boolean;
    reason: string;
    override_score: number;
    override_level: string;
    approved_by?: string;
    approved_at?: string;
  };
}

interface ResidualRiskCalculatorProps {
  riskId?: number;
  processStepId?: number;
  onCalculationComplete?: (result: ResidualRiskResult) => void;
  allowManualOverride?: boolean;
}

export const ResidualRiskCalculator: React.FC<ResidualRiskCalculatorProps> = ({
  riskId: propRiskId,
  processStepId,
  onCalculationComplete,
  allowManualOverride = true
}) => {
  const { riskId: paramRiskId } = useParams<{ riskId: string }>();
  const riskId = propRiskId || (paramRiskId ? parseInt(paramRiskId) : null);
  const [riskData, setRiskData] = useState<RiskData | null>(null);
  const [calculationResult, setCalculationResult] = useState<ResidualRiskResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [isManualOverrideMode, setIsManualOverrideMode] = useState(false);

  // Manual override state
  const [manualOverride, setManualOverride] = useState({
    enabled: false,
    reason: '',
    override_score: 0,
    override_level: '',
    approved_by: '',
    rationale_details: ''
  });

  useEffect(() => {
    if (riskId) {
      fetchRiskData();
    }
  }, [riskId]);

  const fetchRiskData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/residual-risk/risk-data/${riskId}${processStepId ? `?process_step_id=${processStepId}` : ''}`);
      setRiskData(response.data);

      // Check if there's an existing calculation
      if (response.data.existing_calculation) {
        setCalculationResult(response.data.existing_calculation);
        if (response.data.existing_calculation.manual_override?.enabled) {
          setManualOverride(response.data.existing_calculation.manual_override);
          setIsManualOverrideMode(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch risk data:', error);
      toastService.error('Failed to load risk data');
    } finally {
      setLoading(false);
    }
  };

  const calculateResidualRisk = async () => {
    if (!riskData) return;

    setCalculating(true);
    try {
      const response = await api.post('/api/residual-risk/calculate', {
        risk_id: riskId,
        process_step_id: processStepId,
        inherent_likelihood: riskData.inherent_likelihood,
        inherent_impact: riskData.inherent_impact,
        controls: riskData.controls,
        use_ai_enhancement: true
      });

      const result = response.data;
      setCalculationResult(result);
      onCalculationComplete?.(result);

      toastService.success('Residual risk calculated successfully');
    } catch (error) {
      console.error('Failed to calculate residual risk:', error);
      toastService.error('Failed to calculate residual risk');
    } finally {
      setCalculating(false);
    }
  };

  const applyManualOverride = async () => {
    if (!calculationResult || !manualOverride.reason) {
      toastService.error('Please provide a reason for manual override');
      return;
    }

    try {
      const response = await api.post('/api/residual-risk/manual-override', {
        risk_id: riskId,
        process_step_id: processStepId,
        override_data: manualOverride,
        original_calculation: calculationResult
      });

      const updatedResult = {
        ...calculationResult,
        manual_override: response.data.manual_override
      };

      setCalculationResult(updatedResult);
      setIsManualOverrideMode(false);
      onCalculationComplete?.(updatedResult);

      toastService.success('Manual override applied successfully');
    } catch (error) {
      console.error('Failed to apply manual override:', error);
      toastService.error('Failed to apply manual override');
    }
  };

  const removeManualOverride = async () => {
    try {
      await api.delete(`/api/residual-risk/manual-override/${riskId}${processStepId ? `?process_step_id=${processStepId}` : ''}`);

      // Recalculate without override
      await calculateResidualRisk();

      setManualOverride({
        enabled: false,
        reason: '',
        override_score: 0,
        override_level: '',
        approved_by: '',
        rationale_details: ''
      });

      toastService.success('Manual override removed successfully');
    } catch (error) {
      console.error('Failed to remove manual override:', error);
      toastService.error('Failed to remove manual override');
    }
  };

  const getRiskLevelColor = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getEffectivenessColor = (score: number) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading risk data...</span>
      </div>
    );
  }

  if (!riskId) {
    return (
      <div className="text-center p-8 text-gray-500">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">No Risk Selected</h3>
          <p className="text-yellow-700">
            Please select a risk from the RCSA Process Steps page to calculate residual risk.
          </p>
        </div>
      </div>
    );
  }

  if (!riskData) {
    return (
      <div className="text-center p-8 text-gray-500">
        No risk data available for calculation
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="bg-[#221551] text-white px-6 py-4 rounded-t-lg">
        <h3 className="text-lg font-semibold flex items-center">
          <Calculator className="h-5 w-5 mr-2" />
          Residual Risk Calculator
        </h3>
      </div>

      <div className="p-6 space-y-6">
        {/* Risk Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Risk Information
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <span className="text-sm text-gray-600">Risk Name:</span>
              <div className="font-medium text-gray-900">{riskData.risk_name}</div>
            </div>
            <div>
              <span className="text-sm text-gray-600">Inherent Likelihood:</span>
              <div className="font-medium text-gray-900">{riskData.inherent_likelihood}</div>
            </div>
            <div>
              <span className="text-sm text-gray-600">Inherent Impact:</span>
              <div className="font-medium text-gray-900">{riskData.inherent_impact}</div>
            </div>
            <div>
              <span className="text-sm text-gray-600">Inherent Risk Level:</span>
              <div className={`inline-block px-2 py-1 rounded-full text-xs font-semibold border ${getRiskLevelColor(riskData.inherent_risk_level)}`}>
                {riskData.inherent_risk_level}
              </div>
            </div>
          </div>
        </div>

        {/* Controls Summary */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-900 mb-3 flex items-center">
            <Shield className="h-4 w-4 mr-2" />
            Associated Controls ({riskData.controls.length})
          </h4>

          {riskData.controls.length === 0 ? (
            <p className="text-sm text-gray-600">No controls associated with this risk.</p>
          ) : (
            <div className="space-y-3">
              {riskData.controls.map((control, index) => (
                <div key={control.id} className="bg-white border border-green-200 rounded-md p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 mb-1">{control.control_name}</div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Design Effectiveness:</span>
                          <div className={`font-medium ${getEffectivenessColor(control.design_effectiveness_score)}`}>
                            {control.design_effectiveness} ({control.design_effectiveness_score}%)
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Operating Effectiveness:</span>
                          <div className={`font-medium ${getEffectivenessColor(control.operating_effectiveness_score)}`}>
                            {control.operating_effectiveness} ({control.operating_effectiveness_score}%)
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Weight:</span>
                          <div className="font-medium text-gray-900">{control.effectiveness_weight}%</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Calculation Controls */}
        <div className="flex justify-center">
          <button
            onClick={calculateResidualRisk}
            disabled={calculating}
            className="flex items-center gap-2 px-6 py-3 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF] transition-colors disabled:opacity-50 text-lg font-medium"
          >
            <Brain className={`h-5 w-5 ${calculating ? 'animate-spin' : ''}`} />
            {calculating ? 'Calculating...' : 'Calculate Residual Risk'}
          </button>
        </div>

        {/* Calculation Results */}
        {calculationResult && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-purple-900 flex items-center">
                <TrendingUp className="h-4 w-4 mr-2" />
                Residual Risk Calculation Results
              </h4>

              {allowManualOverride && (
                <div className="flex items-center gap-2">
                  {calculationResult.manual_override?.enabled ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                        <Lock className="h-3 w-3 mr-1" />
                        Manual Override Active
                      </span>
                      <button
                        onClick={() => setIsManualOverrideMode(true)}
                        className="p-1 text-gray-600 hover:text-gray-800"
                        title="Edit override"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={removeManualOverride}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Remove override"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsManualOverrideMode(true)}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                    >
                      <Unlock className="h-3 w-3" />
                      Manual Override
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Results Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="bg-white border border-purple-200 rounded-md p-3">
                <span className="text-sm text-gray-600">Residual Likelihood:</span>
                <div className="text-lg font-bold text-purple-600">
                  {calculationResult.manual_override?.enabled ?
                    Math.round(calculationResult.manual_override.override_score / calculationResult.residual_impact) :
                    calculationResult.residual_likelihood
                  }
                </div>
              </div>

              <div className="bg-white border border-purple-200 rounded-md p-3">
                <span className="text-sm text-gray-600">Residual Impact:</span>
                <div className="text-lg font-bold text-purple-600">
                  {calculationResult.residual_impact}
                </div>
              </div>

              <div className="bg-white border border-purple-200 rounded-md p-3">
                <span className="text-sm text-gray-600">Residual Risk Score:</span>
                <div className="text-lg font-bold text-purple-600">
                  {calculationResult.manual_override?.enabled ?
                    calculationResult.manual_override.override_score :
                    calculationResult.residual_risk_score
                  }
                </div>
              </div>

              <div className="bg-white border border-purple-200 rounded-md p-3">
                <span className="text-sm text-gray-600">Residual Risk Level:</span>
                <div className={`inline-block px-2 py-1 rounded-full text-xs font-semibold border ${getRiskLevelColor(
                  calculationResult.manual_override?.enabled ?
                    calculationResult.manual_override.override_level :
                    calculationResult.residual_risk_level
                )}`}>
                  {calculationResult.manual_override?.enabled ?
                    calculationResult.manual_override.override_level :
                    calculationResult.residual_risk_level
                  }
                </div>
              </div>
            </div>

            {/* Control Effectiveness Summary */}
            <div className="bg-white border border-purple-200 rounded-md p-3">
              <h5 className="font-semibold text-gray-900 mb-2">Control Effectiveness Summary</h5>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-gray-600">Total Controls:</span>
                  <div className="font-medium">{calculationResult.control_effectiveness_summary.total_controls}</div>
                </div>
                <div>
                  <span className="text-gray-600">Effective Controls:</span>
                  <div className="font-medium">{calculationResult.control_effectiveness_summary.effective_controls}</div>
                </div>
                <div>
                  <span className="text-gray-600">Overall Effectiveness:</span>
                  <div className={`font-medium ${getEffectivenessColor(calculationResult.control_effectiveness_summary.overall_effectiveness)}`}>
                    {calculationResult.control_effectiveness_summary.overall_effectiveness}%
                  </div>
                </div>
                <div>
                  <span className="text-gray-600">Risk Reduction:</span>
                  <div className="font-medium text-green-600">{calculationResult.risk_reduction_percentage}%</div>
                </div>
              </div>
            </div>

            {/* Manual Override Display */}
            {calculationResult.manual_override?.enabled && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mt-3">
                <h5 className="font-semibold text-yellow-900 mb-2 flex items-center">
                  <Lock className="h-4 w-4 mr-2" />
                  Manual Override Details
                </h5>
                <div className="text-sm text-yellow-800">
                  <div className="mb-2">
                    <strong>Reason:</strong> {calculationResult.manual_override.reason}
                  </div>
                  {calculationResult.manual_override.approved_by && (
                    <div className="mb-2">
                      <strong>Approved by:</strong> {calculationResult.manual_override.approved_by}
                    </div>
                  )}
                  {calculationResult.manual_override.approved_at && (
                    <div>
                      <strong>Approved at:</strong> {new Date(calculationResult.manual_override.approved_at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Manual Override Modal */}
        {isManualOverrideMode && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Manual Risk Override</h3>
                <button
                  onClick={() => setIsManualOverrideMode(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 mb-2">Override Guidelines</h4>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    <li>• Manual overrides should be used sparingly and with proper justification</li>
                    <li>• Provide detailed rationale for the override decision</li>
                    <li>• Consider compensating controls or external factors not captured in the calculation</li>
                    <li>• Overrides may require additional approval based on your organization's policy</li>
                  </ul>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Override Risk Score *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="25"
                      value={manualOverride.override_score}
                      onChange={(e) => setManualOverride(prev => ({ ...prev, override_score: parseFloat(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Override Risk Level *
                    </label>
                    <select
                      value={manualOverride.override_level}
                      onChange={(e) => setManualOverride(prev => ({ ...prev, override_level: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    >
                      <option value="">Select Level</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Override Justification *
                  </label>
                  <textarea
                    value={manualOverride.reason}
                    onChange={(e) => setManualOverride(prev => ({ ...prev, reason: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    placeholder="Provide detailed justification for this manual override..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Rationale Details
                  </label>
                  <textarea
                    value={manualOverride.rationale_details}
                    onChange={(e) => setManualOverride(prev => ({ ...prev, rationale_details: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    placeholder="Additional context, compensating controls, or external factors..."
                  />
                </div>

                {calculationResult && (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <h5 className="font-semibold text-gray-900 mb-2">Current Calculated Values</h5>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Score:</span>
                        <div className="font-medium">{calculationResult.residual_risk_score}</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Level:</span>
                        <div className="font-medium">{calculationResult.residual_risk_level}</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Reduction:</span>
                        <div className="font-medium">{calculationResult.risk_reduction_percentage}%</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 p-6 border-t bg-gray-50">
                <button
                  onClick={() => setIsManualOverrideMode(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={applyManualOverride}
                  disabled={!manualOverride.reason || !manualOverride.override_score || !manualOverride.override_level}
                  className="px-4 py-2 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF] transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  Apply Override
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResidualRiskCalculator;